<script lang="ts">
  import type { InjectionKey } from 'vue'
  import { computed, inject, provide } from 'vue'
  import { useDatabaseSessionStorage } from '../../../database/index'
  import { useSupportSession } from '../../../support-session'
  import { useGsCloudBackend } from '../../../http-client'
  import { API_REQUEST_HEADERS } from '../../../http-client/api-request-headers'
  import { useInvalidateCache } from '../../utils/invalidate-cache'
  import type { SignalRContext } from '@gat-ship/lib/signalr'
  import {
    signalREvent,
    type PermissionsAddedToAccessRoleSignalRPayload,
    type PermissionsRemovedFromAccessRoleSignalRPayload,
    type TenantUserProfileImageChangedSignalRPayload,
    type UserRoleAssignmentAddedSignalRPayload,
    type UserRoleAssignmentRemovedSignalRPayload
  } from '@gat-ship/types/signal-r-events'
  import { useUserAccessControlDataSource } from '../../utils/user-access-control'
  import { useUserProfileDataSource } from '../../utils/user-profile'
  import { useUserPreferences } from '../../utils/user-preferences'
  import {
    AppError,
    ErrorCategory,
    ErrorCode,
    mapToAppError
  } from '../../../error-handling'
  import { useUserStateActiveDb } from '../../utils/user-state-active-db'
  import { useUserStateGlobal } from '../../utils/user-state-global'
  import { _useSignalR } from '../../../signalr'

  export type UserService = ReturnType<typeof createUserContext>

  export type UserServiceContext = ReturnType<typeof createUserContext>

  /** Creates a service provider for the user service. */
  export function createUserContext(signalRContext: SignalRContext) {
    const user = useUserProfileDataSource()

    const gsCloudBackend = useGsCloudBackend()

    const accessControl = useUserAccessControlDataSource(user.isTenantAdmin)

    const userPreferences = useUserPreferences({ userId: user.id })

    // Use centralized database storage
    const {
      hasMultipleDatabases,
      databaseInfo,
      selectDatabase,
      fetchDatabases,
      removeDatabase: _removeDatabase,
      addDatabase: _addDatabase,
      hasDatabaseAccess
    } = useDatabaseSessionStorage()

    // Use centralized support session storage
    const { supportSessionId, isSupportSession: isSupportSessionFromStorage } =
      useSupportSession({
        inject: false
      })

    // Support session detection can come from user profile OR session storage
    const isSupportSession = computed(() => {
      return (
        user.profile.value?.actor !== undefined ||
        isSupportSessionFromStorage.value
      )
    })

    // Use computed database info from centralized storage
    const databases = computed(() => {
      return databaseInfo.value
    })

    /** Changes the current database and reloads the page. */
    function switchDatabase(id: string) {
      // Use centralized database selection
      const selectedDb = selectDatabase(id)
      if (selectedDb) {
        window.location.reload()
      }
    }

    async function _loadUser() {
      let tenantId: string = ''
      let tenantName: string = ''
      await user.fetchUserProfile()
      tenantId = user.profile.value?.homeTenantId ?? ''
      tenantName = user.profile.value?.homeTenantName ?? ''
      await userPreferences.fetchUserPreferences()
      try {
        const { tenantId: fetchedTenantId } = await fetchDatabases()
        tenantId = fetchedTenantId
        gsCloudBackend.setAxiosHeader(
          API_REQUEST_HEADERS.GATSHIP_ACTIVE_TENANT_DB_INSTANCE,
          tenantId
        )
      } catch (err) {
        const error = mapToAppError(err)
        console.log('error', error)
        if (
          error.code === ErrorCode.DB_ACCESS_MISSING &&
          !user.isTenantAdmin.value
        ) {
          // Will show the app error screen.
        }
        throw error
      }
      if (hasDatabaseAccess.value) {
        await Promise.all([accessControl.fetchAccessCodes()])
      }
      const currentDatabase = databaseInfo.value.current
      if (currentDatabase) {
        tenantName = currentDatabase.tenantName
      }
      const userProfile = user.profile.value
      if (!userProfile) {
        throw new AppError({
          category: ErrorCategory.ACCESS,
          code: ErrorCode.USER_NOT_FOUND,
          message: 'No user profile found',
          retryable: false
        })
      }

      signalRContext.subscribe<PermissionsAddedToAccessRoleSignalRPayload>(
        signalREvent.AccessControl.V1.PermissionsAddedToAccessRole,
        ({ message }) => {
          if (accessControl.hasRole(message.roleId)) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<PermissionsRemovedFromAccessRoleSignalRPayload>(
        signalREvent.AccessControl.V1.PermissionsRemovedFromAccessRole,
        ({ message }) => {
          if (accessControl.hasRole(message.roleId)) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<UserRoleAssignmentAddedSignalRPayload>(
        signalREvent.AccessControl.V1.UserRoleAssigned,
        ({ message }) => {
          if (message.userId === user.id.value) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<UserRoleAssignmentRemovedSignalRPayload>(
        signalREvent.AccessControl.V1.UserRoleUnassigned,
        ({ message }) => {
          if (message.userId === user.id.value) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<TenantUserProfileImageChangedSignalRPayload>(
        signalREvent.AccessControl.V1.TenantUserProfileImageChanged,
        ({ message }) => {
          if (message.userId === user.id.value) {
            user.fetchUserProfile()
          }
        }
      )
      return {
        tenantId,
        tenantName,
        databaseId: currentDatabase?.id,
        databaseName: currentDatabase?.name,
        userId: user.id.value,
        userEmail: userProfile.email
      }
    }

    const cacheInvalidation = useInvalidateCache()
    // Initialize cooldown on service creation
    cacheInvalidation.initializeCooldown()

    const userStateActiveDb = useUserStateActiveDb()
    const userStateGlobal = useUserStateGlobal()
    return {
      id: user.id,
      _loadUser,
      _removeDatabase,
      _addDatabase,
      userProfile: user.profile,
      hasPermission: accessControl.hasPermission,
      hasAgencyPermissions: accessControl.hasAgencyPermissions,
      hasMultiDBAccess: hasMultipleDatabases,
      databases,
      isTenantAdmin: user.isTenantAdmin,
      isSupportUser: user.isSupportUser,
      isSupportSession,
      supportSessionId,
      /** Changes the current database and reloads the page. */
      switchDatabase,
      fetchDatabases,
      /** Invalidates the cache and reloads the page.
       * @param options - The options for the invalidate cache operation.
       * @param options.reloadPage - Whether to reload the page after invalidating the cache. @default true
       */
      cacheInvalidation,
      invalidateCache: cacheInvalidation.invalidateCache,
      isCacheCooldownActive: cacheInvalidation.isCooldownActive,
      cacheCooldownRemainingSeconds: cacheInvalidation.remainingCooldownSeconds,
      userPreferences,
      userState: {
        /** The user state for the global scope. */
        global: userStateGlobal,
        /** The user state for the active database scope. */
        activeDb: userStateActiveDb
      }
    }
  }

  export const userServiceKey = Symbol(
    'gatship-user-service'
  ) as InjectionKey<UserService>

  export function useUser() {
    const userService = inject(userServiceKey)

    if (!userService) {
      throw new Error(
        'could not find user-service provider. Did you provide it in your app?'
      )
    }
    return userService
  }
</script>
<script setup lang="ts">
  const signalRContext = _useSignalR()
  const userContext = createUserContext(signalRContext)
  provide(userServiceKey, userContext)
  defineExpose(userContext)
</script>
<template>
  <slot />
</template>
